/**
 * @file AccountForm.jsx
 * @description Handles account information collection and validation during onboarding
 *
 * @section Responsibilities
 * - Collect account details
 * - Validate account information
 * - Handle form submission
 * - Manage form state
 * - Process account creation
 * - Handle validation errors
 *
 * @section Dependencies
 * - FormValidation: Input validation
 * - AccountService: Account management
 * - ValidationService: Data validation
 * - StateManagement: Form state handling
 *
 * @section Integration Points
 * - Account API: Account creation
 * - Validation System: Data validation
 * - Onboarding Flow: Progress tracking
 * - Navigation System: Form routing
 *
 * @section State Management
 * - formData: Account information
 * - validationState: Form validation
 * - submissionStatus: Form submission
 * - errorState: Error handling
 *
 * @section Events
 * - onInputChange: Form field updates
 * - onValidation: Field validation
 * - onSubmit: Form submission
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} initialData - Initial form data
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} validationRules - Form validation rules
 * @prop {Boolean} autoValidate - Controls automatic validation
 */

import React, { useEffect, useState } from "react";
import SuitabilityProfile from "./ApplicationQuestions/SuitabilityProfile";
import InvestmentProfile from "./ApplicationQuestions/InvestmentProfile";
import Applicants from "./ApplicationQuestions/Applicants/Applicants";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import Beneficiaries from "./Beneficiaries";

function AccountForm({ actType, sendAccountData }) {
  const { applicationType } = useApplicationContext();

  const emptyAccount = {
    tradeAuthorization: {
      isTradeAuthorization: "YES",
      agentName: "Tradier",
    },
    accountType: actType,
    customerType: "",
    suitabilityProfile: {
      timeHorizon: "",
      liquidityNeeds: "",
    },
    investmentProfile: {
      totalNetWorthUSD: {
        min: null,
        max: null,
      },
      liquidNetWorthUSD: {
        min: null,
        max: null,
      },
      annualIncomeUSD: {
        min: null,
        max: null,
      },
      investmentObjective: "",
      riskTolerance: "",
      investmentExperience: "",
    },
    applicants: [{}],
  };

  const [accountData, setAccountData] = useState(emptyAccount);
  const [suitabilityProfile, setSuitabilityProfile] = useState({});
  const [investmentProfile, setInvestmentProfile] = useState({});

  const handleSuitabilityProfile = (data) => {
    setSuitabilityProfile(data);
    handleChange("suitabilityProfile", data);
  };

  const handleInvestmentProfile = (data) => {
    setInvestmentProfile(data);
    handleChange("investmentProfile", data);
  };

  const handleApplicants = (data) => {
    setInvestmentProfile(data);
    setAccountData((prevState) => {
      const updatedApplicants = [...prevState.applicants];
      updatedApplicants[0] = data; // Actualiza el índice 0
      return {
        ...prevState,
        applicants: updatedApplicants,
      };
    });
  };

  const handleNextApplicants = (data) => {
    setInvestmentProfile(data);
    setAccountData((prevState) => {
      const updatedApplicants = [...prevState.applicants];
      if (updatedApplicants.length < 2) {
        updatedApplicants.push(data); // Agrega un nuevo índice si aún no existe
      } else {
        updatedApplicants[1] = data; // Actualiza el índice 1 si ya existe
      }
      return {
        ...prevState,
        applicants: updatedApplicants,
      };
    });
  };

  const handleBeneficiaries = (data) => {
    setAccountData((prevState) => ({
      ...prevState,
      retirementAccountBeneficiariesForm:
        data.retirementAccountBeneficiariesForm,
    }));
  };

  const handleChange = (field, value) => {
    setAccountData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  useEffect(() => {
    sendAccountData(accountData);
  }, [accountData]);

  useEffect(() => {
    handleChange("accountType", actType);
  }, [actType]);

  return (
    <>
      <p className="subtitle">Investment profile</p>
      <SuitabilityProfile sendSuitabilityProfile={handleSuitabilityProfile} />
      <InvestmentProfile sendInvestmentProfile={handleInvestmentProfile} />
      <p className="subtitle">Personal Information</p>
      {applicationType === "joint_cash_survivor" ||
      applicationType === "joint_margin_survivor" ? (
        <>
          <Applicants sendApplicants={handleApplicants} />
          <p className="subtitle">Second Personal Information</p>
          <Applicants sendApplicants={handleNextApplicants} />
        </>
      ) : (
        <Applicants sendApplicants={handleApplicants} />
      )}
      {(applicationType === "rollover_ira" ||
        applicationType === "roth_ira" ||
        applicationType === "traditional_ira") && (
        <Beneficiaries sendBeneficiaries={handleBeneficiaries} />
      )}
    </>
  );
}

export default AccountForm;
